import React, { useContext, useEffect, useState } from "react";
import jwtDecode from "jwt-decode";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import { CommonText } from "components/common/text/text.component";
import DialogTitle from "@mui/material/DialogTitle";
import Box from "@mui/material/Box";
import { useSWRConfig } from "swr";
import { Card } from "@mui/material";
import { FormInput, FieldError } from "components/common/Input";
import { AuthButton, TextButton, GhostButton } from "components/common/Buttons";
import LogoBlue from "assets/svg/nextivity_logo_color.svg";
import { showError } from "utils/notifications";
import { emailRegex } from "utils/constants";
import { passwordStrengthCheck } from "utils/util-functions";
import { useAuth } from "hooks/useProvideAuth";
import { GlobalContext } from "context/GlobalContext";
import { PageFooter } from "components/common/Wrapper";
import { FormWrapper, NewMember, PageContainer, LogoImg, AccountInfo } from "./Login.style";
import { authenticateUser } from "../Cogcognito";
import LOGIN_STEP from "./Login.config";

const NEW_MEGA_PORTAL_PATH = "https://megaportal.nextivityinc.net";

const Login = () => {
  const navigate = useNavigate();
  const [isAuthLoading, setIsAuthLoading] = useState(false);
  const [isMfaLoading, setIsMfaLoading] = useState(false);
  const [isNewPwdLoading, setIsNewPwdLoading] = useState(false);
  const [loginStep, setLoginStep] = useState(LOGIN_STEP.AUTH);
  const [mfaParam, setMfaParam] = useState({});
  const [newPasswordParam, setNewPasswordParam] = useState({});
  const [isOpenAccountModal, setIsOpenAccountModal] = useState(false);

  const { setIsDirectFromLogin } = useContext(GlobalContext);

  const { refetch } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({});

  const {
    register: registerMfa,
    handleSubmit: handleSubmitMfa,
    formState: { errors: mfaErrors },
  } = useForm({});

  const {
    register: registerNewPassword,
    handleSubmit: handleSubmitNewPassword,
    formState: { errors: newPasswordErrors },
    watch,
  } = useForm({});
  const newPassword = watch("newPassword", "");

  const { cache } = useSWRConfig();

  useEffect(() => {
    cache.clear();
  }, []);

  const mfaCallback = (cognitoUser, mfaFunction) => {
    setIsAuthLoading(false);

    setMfaParam({ cognitoUser, mfaFunction });
    setLoginStep(LOGIN_STEP.MFA);
  };

  const newPasswordCallback = (cognitoUser, userAttributes, newPasswordFunction) => {
    setIsAuthLoading(false);

    setNewPasswordParam({ cognitoUser, userAttributes, newPasswordFunction });
    setLoginStep(LOGIN_STEP.NEW_PASSWORD);
  };

  const onSubmit = (data) => {
    const { email, password } = data;
    window.location.href = "/login";

/*     setIsAuthLoading(true);

    authenticateUser(
      email,
      password,
      (err, result) => {
        setIsAuthLoading(false);
        setIsMfaLoading(false);
        setIsNewPwdLoading(false);

        if (err) {
          if (err.name === "UserNotConfirmedException") {
            localStorage.setItem("destinationForVerify", email);
            navigate("/verify-email");
          } else {
            showError(err);
          }
          return;
        }

        localStorage.setItem("apiFailCount", 0);
        localStorage.setItem("id", result.getIdToken().getJwtToken());
        localStorage.setItem("access", result.getAccessToken().getJwtToken());

        const decoded = jwtDecode(localStorage.getItem("id"));
        if (!decoded.email_verified) {
          localStorage.setItem("destinationForVerify", decoded.email);
          navigate("/verify-email");
        } else {
          refetch?.()
            .then(({ isAdmin, isPowerUser }) => {
              setIsDirectFromLogin(true);
              navigate(isPowerUser ? "/orgs" : isAdmin ? "/users" : "/devices");
            })
            .catch((error) => {
              showError(error.toString());
            });
        }
      },
      mfaCallback,
      newPasswordCallback,
    ); */
  };

  const onSubmitMfa = (data) => {
    const { mfaCode } = data;
    const { cognitoUser, mfaFunction } = mfaParam;
    if (cognitoUser && mfaFunction) {
      setIsMfaLoading(true);
      cognitoUser.sendMFACode(mfaCode, mfaFunction);
    }
  };

  const onSubmitNewPassword = (data) => {
    const { newPassword: newPwd } = data;

    const { cognitoUser, userAttributes, newPasswordFunction } = newPasswordParam;
    if (cognitoUser && userAttributes && newPasswordFunction) {
      setIsNewPwdLoading(true);

      delete userAttributes.email;
      delete userAttributes.phone_number;
      cognitoUser.completeNewPasswordChallenge(newPwd, userAttributes, newPasswordFunction);
    }
  };

  const showAccountInfo = () => {
    setIsOpenAccountModal(true);
  };

  const onModalClose = (event, reason) => {
    if (reason !== "backdropClick") {
      setIsOpenAccountModal(false);
    }
  };

  return (
    <PageContainer>
      {loginStep === LOGIN_STEP.AUTH && (
        <>
          <Card sx={{ marginBottom: "56px", padding: "16px" }}>
            <CommonText>
              This website has moved. Please start using the site{" "}
              <Link to={NEW_MEGA_PORTAL_PATH}>{NEW_MEGA_PORTAL_PATH}</Link>. https://beta.awcone.com is no longer
              available.
            </CommonText>
          </Card>
          <FormWrapper onSubmit={handleSubmit(onSubmit)}>
            <LogoImg src={LogoBlue} alt="logo" />
            <FormInput
              placeholder="Email"
              {...register("email", {
                required: "Email is required!",
                pattern: {
                  value: emailRegex,
                  message: "Entered value does not match email format!",
                },
              })}
              error={errors.email}
            />
            {errors.email && <FieldError>{errors.email.message}</FieldError>}

            <FormInput
              placeholder="Password"
              type="password"
              {...register("password", {
                required: "Password is required!",
              })}
              style={{ marginTop: 20 }}
              error={errors.password}
            />
            {errors.password && <FieldError>{errors.password.message}</FieldError>}

            <AuthButton loading={isAuthLoading} onClick={handleSubmit(onSubmit)} sx={{ mt: 2 }}>
              Sign In
            </AuthButton>

            <NewMember>
              New to Nextivity? <TextButton onClick={() => showAccountInfo()}>Create an account</TextButton>
              .<br />
              <TextButton onClick={() => navigate("/forgot-password")}>Forgot password?</TextButton>
            </NewMember>
          </FormWrapper>
        </>
      )}

      {loginStep === LOGIN_STEP.MFA && (
        <FormWrapper onSubmit={handleSubmitMfa(onSubmitMfa)}>
          <LogoImg src={LogoBlue} alt="logo" />
          <NewMember style={{ marginBottom: 15 }}>
            Please input the MFA code sent to your phone for verification.
          </NewMember>
          <FormInput
            placeholder="MFA Code"
            {...registerMfa("mfaCode", {
              required: "MFA Code is required!",
            })}
            error={mfaErrors.mfaCode}
          />
          {mfaErrors.mfaCode && <FieldError>{mfaErrors.mfaCode.message}</FieldError>}

          <AuthButton isLoading={isMfaLoading} onClick={handleSubmitMfa(onSubmitMfa)} sx={{ mt: 2 }}>
            Submit
          </AuthButton>
        </FormWrapper>
      )}

      {loginStep === LOGIN_STEP.NEW_PASSWORD && (
        <FormWrapper onSubmit={handleSubmitNewPassword(onSubmitNewPassword)}>
          <LogoImg src={LogoBlue} alt="logo" />
          <NewMember style={{ marginBottom: 15 }}>Please input new password.</NewMember>
          <FormInput
            placeholder="New Password"
            type="password"
            {...registerNewPassword("newPassword", {
              required: "New Password is required!",
              validate: {
                passwordStrengthCheck,
              },
            })}
            style={{ marginTop: 20 }}
            error={newPasswordErrors.newPassword}
          />
          {newPasswordErrors.newPassword && <FieldError>{newPasswordErrors.newPassword.message}</FieldError>}

          <FormInput
            placeholder="Confirm New Password"
            type="password"
            {...registerNewPassword("confirmNewPassword", {
              required: "Confirm Password is required!",
              validate: (value) => value === newPassword || "Passwords do not match!",
            })}
            style={{ marginTop: 20 }}
            error={newPasswordErrors.confirmNewPassword}
          />
          {newPasswordErrors.confirmNewPassword && (
            <FieldError>{newPasswordErrors.confirmNewPassword.message}</FieldError>
          )}

          <AuthButton isLoading={isNewPwdLoading} onClick={handleSubmitNewPassword(onSubmitNewPassword)} sx={{ mt: 2 }}>
            Submit
          </AuthButton>
        </FormWrapper>
      )}

      <Dialog fullWidth maxWidth="sm" open={isOpenAccountModal} onClose={onModalClose} disableEscapeKeyDown>
        <DialogTitle>New Account</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <AccountInfo>
              If your organization already has an account, contact your organization’s administrator to create a user
              account.
              <br /> <br /> If your organization does not yet have an account, contact{" "}
              <a href="mailto:support@nextivityinc.com">support@nextivityinc.com</a> to create an administrative account
              for your organization. <br />
              <br />
              Please provide:
              <ul>
                <li>Organization name</li>
                <li>Your Name</li>
                <li>Mobile Phone Number</li>
                <li>Email</li>
              </ul>
            </AccountInfo>

            <PageFooter>
              <GhostButton onClick={() => setIsOpenAccountModal(false)}>Close</GhostButton>
            </PageFooter>
          </Box>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
};

export default Login;
